import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import Header from '../components/Header';
import { useSpinner } from '../context/SpinnerContext';
import { useThemeContext } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';

const PageWrapper = styled.div`
  padding: 40px 20px;
  background: ${({ theme }) => theme.background || '#f9f9f9'};
  min-height: 100vh;
  font-family: ${({ theme }) => theme.font || 'sans-serif'};
`;

const Panel = styled.div`
  padding: 2rem;
  background: ${({ theme }) => theme.card || '#fff'};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 900px;
  margin: 0 auto;
`;

const Title = styled.h2`
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.text || '#111'};
`;

const Section = styled.section`
  margin-top: 1.25rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.section || '#f0f0f0'};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.text || '#111'};
  margin-top: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
  background: ${({ theme }) => theme.card || '#fff'};
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
  background: ${({ theme }) => theme.card || '#fff'};
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
  background: ${({ theme }) => theme.card || '#fff'};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.text || '#111'};
`;

const Button = styled.button`
  padding: 10px 16px;
  background-color: ${({ theme }) => theme.primary || '#007bff'};
  color: ${({ theme }) => theme.buttonText || 'white'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  min-width: 140px;

  &:hover {
    background-color: ${({ theme }) => theme.primaryHover || '#0069d9'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${({ theme }) => theme.text || '#111'};
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const ErrorText = styled.p`
  color: #b00020;
  margin-top: 12px;
`;

const SuccessBox = styled.div`
  margin-top: 14px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
  background: ${({ theme }) => theme.card || '#fff'};
`;

function slugify(input) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

const CreateBlogPage = () => {
  const navigate = useNavigate();
  const { setLoading } = useSpinner();
  const { theme } = useThemeContext();

  const [themes, setThemes] = useState([]);
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [themeId, setThemeId] = useState('');
  const [allowComments, setAllowComments] = useState(true);
  const [allowReactions, setAllowReactions] = useState(true);

  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const autoSlug = useMemo(() => slugify(title), [title]);
  const { refreshBlogs } = useUser();

  useEffect(() => {
    // Only auto-fill slug if user hasn't typed something custom
    setSlug((prev) => (prev?.trim() ? prev : autoSlug));
  }, [autoSlug]);

  useEffect(() => {
    const loadThemes = async () => {
      try {
        const res = await axios.get('/api/themes/');
        setThemes(Array.isArray(res.data) ? res.data : []);
        // pick first as default if none chosen
        if (!themeId && Array.isArray(res.data) && res.data.length > 0) {
          setThemeId(String(res.data[0].id));
        }
      } catch (e) {
        // non-fatal
        console.warn('Failed to load themes:', e);
      }
    };
    loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    const s = slugify(slug);
    if (!title.trim()) return 'Title is required.';
    if (!s) return 'Slug is required.';
    if (s !== slug.trim()) return 'Slug must be lowercase letters/numbers/dashes only.';
    return '';
  };

  const handleCreate = async () => {
    setError('');
    setCreated(null);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setCreating(true);
    setLoading(true);

    try {
      // Platform admin create endpoint (recommended):
      // POST /api/admin/blogs/
      const payload = {
        title: title.trim(),
        slug: slugify(slug),
        name: name.trim() || undefined,
        description: description || undefined,
        allow_comments: allowComments,
        allow_reactions: allowReactions,
        theme_id: themeId ? Number(themeId) : undefined,
      };

      const res = await axios.post('/api/admin/blogs/', payload);
      setCreated(res.data);
      await refreshBlogs();
      
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.slug?.[0] ||
        err?.response?.data?.title?.[0] ||
        'Failed to create blog.';
      setError(String(msg));
    } finally {
      setCreating(false);
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        hidePlatformAdmin
        customButtons={
          <SecondaryButton theme={theme} onClick={() => navigate('/platform-admin')}>
            ← Back to Platform Admin
          </SecondaryButton>
        }
      />

      <PageWrapper theme={theme}>
        <Panel theme={theme}>
          <Title theme={theme}>Create Blog</Title>

          <Section theme={theme}>
            <Label theme={theme}>Title</Label>
            <Input
              theme={theme}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. JD & Natasha's Honeymoon Adventures"
            />

            <Row>
              <div style={{ flex: 1, minWidth: 260 }}>
                <Label theme={theme}>Slug</Label>
                <Input
                  theme={theme}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="e.g. blog-alpha"
                />
                <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                  URL-friendly id. Lowercase, numbers, dashes only.
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 260 }}>
                <Label theme={theme}>Internal Name (optional)</Label>
                <Input
                  theme={theme}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Blog Alpha"
                />
              </div>
            </Row>

            <Label theme={theme}>Description (optional)</Label>
            <Textarea
              theme={theme}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short intro shown on the blog header."
            />

            <Row>
              <div style={{ flex: 1, minWidth: 260 }}>
                <Label theme={theme}>Default Theme</Label>
                <Select theme={theme} value={themeId} onChange={(e) => setThemeId(e.target.value)}>
                  {themes.length === 0 ? (
                    <option value="">(No themes found)</option>
                  ) : (
                    themes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </Row>

            <Row>
              <CheckboxLabel theme={theme}>
                <input
                  type="checkbox"
                  checked={allowComments}
                  onChange={() => setAllowComments((v) => !v)}
                />
                Allow comments
              </CheckboxLabel>

              <CheckboxLabel theme={theme}>
                <input
                  type="checkbox"
                  checked={allowReactions}
                  onChange={() => setAllowReactions((v) => !v)}
                />
                Allow reactions
              </CheckboxLabel>
            </Row>

            <Row style={{ marginTop: 16 }}>
              <Button theme={theme} disabled={creating} onClick={handleCreate}>
                {creating ? 'Creating…' : 'Create Blog'}
              </Button>

              <SecondaryButton
                theme={theme}
                disabled={creating}
                onClick={() => {
                  setTitle('');
                  setSlug('');
                  setName('');
                  setDescription('');
                  setAllowComments(true);
                  setAllowReactions(true);
                  setError('');
                  setCreated(null);
                }}
              >
                Reset Form
              </SecondaryButton>
            </Row>

            {error && <ErrorText>{error}</ErrorText>}

            {created && (
              <SuccessBox theme={theme}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>✅ Blog created</div>
                <div style={{ lineHeight: 1.5 }}>
                  <div>
                    <strong>id:</strong> {created.id ?? '(unknown)'}
                  </div>
                  <div>
                    <strong>slug:</strong> {created.slug ?? slugify(slug)}
                  </div>
                  <div>
                    <strong>title:</strong> {created.title ?? title}
                  </div>
                </div>

                <Row style={{ marginTop: 12 }}>
                  <Button
                    theme={theme}
                    onClick={() => {
                      // Keep this simple: go back to platform admin list.
                      // You can also deep-link to a platform blog detail page later if you add one.
                      navigate('/platform-admin');
                    }}
                  >
                    Back to Platform Admin
                  </Button>
                </Row>
              </SuccessBox>
            )}
          </Section>
        </Panel>
      </PageWrapper>
    </>
  );
};

export default CreateBlogPage;
